import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { BFLogo } from "./BFLogo";

const NAV_PATHS = {
  home: "/",
  products: "/products",
  news: "/news",
  about: "/about",
  reporting: "/reporting",
};

function LangSwitcher({ compact = false }) {
  const { lang, setLang } = useApp();
  return (
    <div
      data-testid="lang-switcher"
      className="relative inline-flex items-center rounded-full border border-[#002355]/20 bg-white/70 p-0.5"
    >
      {["ru", "kz"].map((l) => (
        <button
          key={l}
          data-testid={`lang-btn-${l}`}
          onClick={() => setLang(l)}
          className="relative z-10 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide transition-colors duration-300"
          style={{ color: lang === l ? "#f5f7fa" : "#002355" }}
        >
          {l}
        </button>
      ))}
      <motion.span
        layout
        className="absolute top-0.5 bottom-0.5 z-0 rounded-full bg-[#002355]"
        style={{ width: "calc(50% - 2px)", left: lang === "ru" ? "2px" : "50%" }}
        transition={{ type: "spring", stiffness: 400, damping: 32 }}
      />
    </div>
  );
}

export function Header() {
  const { c, openApply } = useApp();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const go = (id) => {
    setMenuOpen(false);
    navigate(NAV_PATHS[id] || "/");
  };

  return (
    <>
      <motion.header
        data-testid="site-header"
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
        className="fixed top-0 left-0 right-0 z-50 bg-[#f5f7fa] shadow-[0_8px_30px_-18px_rgba(0, 35, 85,0.4)]"
      >
        <div className="mx-auto flex h-[72px] max-w-[1240px] items-center justify-between px-5 md:px-8">
          <button data-testid="logo-home" onClick={() => go("home")} className="transition-opacity hover:opacity-80">
            <BFLogo
              brand={c.brand}
              size={110}
              className="-ml-2"
            />
          </button>

          <nav className="hidden items-center gap-1 lg:flex">
            {c.nav.map((item) => (
              <button
                key={item.id}
                data-testid={`nav-${item.id}`}
                onClick={() => go(item.id)}
                className="relative rounded-full px-4 py-2 text-base font-medium text-[#002355] transition-colors duration-300 after:absolute after:bottom-1 after:left-4 after:right-4 after:h-px after:origin-left after:scale-x-0 after:bg-[#e8a93d] after:transition-transform after:duration-300 hover:text-[#002355] hover:after:scale-x-100"
              >
                {item.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <LangSwitcher />
            <button
              data-testid="header-apply-btn"
              onClick={openApply}
              className="hidden rounded-full bg-[#e8a93d] px-5 py-2.5 text-sm font-bold text-[#002355] shadow-[0_10px_24px_-10px_rgba(232,169,61,0.9)] transition-transform duration-300 hover:scale-105 sm:inline-flex"
            >
              {c.cta.apply}
            </button>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMenuOpen((o) => !o)}
              className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#002355]/20 text-[#002355] lg:hidden"
              aria-label="menu"
            >
              {menuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-[#002355] pt-24 lg:hidden"
          >
            <div className="grain absolute inset-0 opacity-40" />
            <nav className="relative flex flex-col gap-2 px-6">
              {c.nav.map((item, i) => (
                <motion.button
                  key={item.id}
                  data-testid={`mobile-nav-${item.id}`}
                  onClick={() => go(item.id)}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  className="border-b border-white/10 py-4 text-left font-display text-3xl font-semibold tracking-tight text-[#f5f7fa]"
                >
                  {item.label}
                </motion.button>
              ))}
              <button
                data-testid="mobile-apply-btn"
                onClick={() => { setMenuOpen(false); openApply(); }}
                className="mt-6 rounded-full bg-[#e8a93d] px-6 py-4 text-base font-bold text-[#002355]"
              >
                {c.cta.apply}
              </button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
